(() => {
  'use strict';
  if (window.SnugCartMutations) return;

  let queue = Promise.resolve();
  let lastState = null;
  let pending = 0;
  const removals = new Map();
  const checkoutButtons = new Map();
  const root = () => window.Shopify?.routes?.root || '/';
  const cartUrl = () => `${root()}cart.js`;
  const changeUrl = () => window.routes?.cart_change_url || `${root()}cart/change.js`;
  const properties = (value) => {
    try { return typeof value === 'string' ? JSON.parse(value || '{}') : value || {}; }
    catch (_) { return {}; }
  };
  const signature = (value) => JSON.stringify(Object.entries(properties(value)).sort(([a], [b]) => a.localeCompare(b)));
  const planId = (item) => String(item.selling_plan_allocation?.selling_plan?.id || '');
  const identityFor = (row, key) => {
    const input = row?.querySelector('[name="updates[]"]');
    const lineKey = row?.dataset.snugKey || key || input?.dataset.id || '';
    return {
      key: String(lineKey),
      variant: String(row?.dataset.snugVariantId || String(lineKey).split(':')[0]),
      properties: properties(row?.dataset.snugProperties),
      plan: String(row?.dataset.snugSellingPlan || '')
    };
  };

  // Keys change when automatic discounts change. Match the exact bundle slot,
  // including private properties and selling plan, before using a new key.
  const resolveKey = (identity, state) => {
    const exact = state.items?.find((item) => item.key === identity.key);
    if (exact) return exact.key;
    const matches = (state.items || []).filter((item) => (
      String(item.variant_id || item.id) === identity.variant
      && signature(item.properties) === signature(identity.properties)
      && planId(item) === identity.plan
    ));
    if (matches.length > 1) throw new Error('Please refresh your cart before updating this item.');
    return matches[0]?.key || null;
  };

  const request = async (url, options = {}) => {
    const response = await fetch(url, {
      ...options,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.body ? { 'Content-Type': 'application/json' } : {}) }
    });
    const state = await response.json();
    if (!response.ok || state.errors || state.status >= 400) {
      const message = typeof state.errors === 'string' ? state.errors : state.description || state.message;
      const error = new Error(message || 'Unable to update the cart. Please try again.');
      error.status = response.status;
      throw error;
    }
    return state;
  };

  const sectionsFor = () => {
    const result = [];
    if (document.getElementById('minicart-form')) result.push({ id: 'minicart-form', section: 'minicart-form' });
    const page = document.getElementById('main-cart-items');
    if (page) result.push({ id: 'main-cart-items', section: page.dataset.id || 'main-cart-items' });
    return result;
  };

  const lockCheckout = () => {
    if (!pending) return;
    document.querySelectorAll('[name="checkout"]').forEach((button) => {
      if (!checkoutButtons.has(button)) checkoutButtons.set(button, button.disabled);
      button.disabled = true;
    });
  };
  const unlockCheckout = () => {
    if (pending) return;
    checkoutButtons.forEach((disabled, button) => { if (button.isConnected) button.disabled = disabled; });
    checkoutButtons.clear();
  };
  const showError = (error, row) => {
    const anchor = row?.isConnected
      ? row.querySelector('.cart-item__details, .minicart__product-info') || row
      : document.querySelector('.minicart__wrapper.open, #main-cart-items, .minicart__wrapper');
    if (!anchor) return;
    let note = anchor.querySelector('[data-snug-cart-error]');
    if (!note) {
      note = document.createElement('p');
      note.dataset.snugCartError = 'true';
      note.className = 'form__message error snug-cart-inline-message';
      note.setAttribute('role', 'alert');
      anchor.appendChild(note);
    }
    note.textContent = error.message;
  };

  const render = async (state, sections) => {
    // A cart mutation can succeed while a bundled section fails to render.
    // Fetch only the missing HTML; never repeat the successful mutation.
    let html = state.sections || {};
    const missing = sections.filter((section) => !html[section.section]);
    if (missing.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', missing.map((section) => section.section).join(','));
      html = { ...html, ...await request(url.toString()) };
    }
    for (const section of sections) {
      const target = document.getElementById(section.id);
      if (!target) continue;
      const fresh = new DOMParser().parseFromString(html[section.section] || '', 'text/html').getElementById(section.id);
      if (!fresh) {
        window.location.reload();
        return;
      }
      target.innerHTML = fresh.innerHTML;
    }
    document.querySelectorAll('.cart-count').forEach((node) => {
      node.textContent = state.item_count > 100 && !node.classList.contains('cart-count-drawer') ? '~' : String(state.item_count);
    });
    // Optional theme widgets must not interrupt a successful cart refresh.
    try {
      const cart = document.querySelector('cart-notification');
      if (cart) {
        cart.currentItemCount = state.item_count;
        cart.cartCountDown = cart.querySelector('.cart-countdown-time');
        cart.cartUpsellBeside = cart.querySelector('.recommendations-beside');
        cart.cartAction?.();
      }
      document.querySelector('header-total-price')?.updateTotal?.(state);
      document.querySelectorAll('free-ship-progress-bar').forEach((bar) => bar.init?.(state.items_subtotal_price));
    } catch (_) { /* Shopify-rendered rows and totals are already current. */ }
    lockCheckout();
    document.dispatchEvent(new CustomEvent('snug:cart-updated', { detail: state }));
  };

  const change = ({ key, line, quantity, target }) => {
    const row = target?.closest('.cart-item') || document.getElementById(`minicart__product-${key}`) || document.getElementById(`CartItem-${line}`);
    const identity = identityFor(row, key);
    quantity = Number(quantity);
    if (!identity.key || !Number.isInteger(quantity) || quantity < 0) {
      showError(new Error('Please enter a whole-number quantity of zero or more.'), row);
      return Promise.resolve(null);
    }
    const token = identity.key;
    if (quantity === 0 && removals.has(token)) return removals.get(token);
    const wasQueued = pending > 0;
    pending += 1;
    row?.setAttribute('aria-busy', 'true');
    lockCheckout();

    const result = queue.catch(() => {}).then(async () => {
      document.querySelectorAll('[data-snug-cart-error]').forEach((note) => note.remove());
      const sections = sectionsFor();
      let currentKey = wasQueued && lastState ? resolveKey(identity, lastState) : identity.key;
      const post = (id) => request(changeUrl(), {
        method: 'POST',
        body: JSON.stringify({ id, quantity, sections: sections.map((section) => section.section), sections_url: window.location.pathname })
      });
      let state;
      if (!currentKey) {
        if (quantity !== 0) throw new Error('That item is no longer in your cart.');
        state = lastState;
      } else {
        try { state = await post(currentKey); }
        catch (error) {
          // Only a missing/stale-key response is safe to retry. A timeout or a
          // stock error must not silently repeat a possibly successful change.
          if (![400, 404].includes(error.status)) throw error;
          const current = await request(cartUrl());
          currentKey = resolveKey(identity, current);
          if (!currentKey && quantity === 0) state = current;
          else if (currentKey && currentKey !== identity.key) state = await post(currentKey);
          else throw error;
        }
      }
      lastState = state;
      await render(state, sections);
      return state;
    }).catch((error) => {
      const input = row?.querySelector('[name="updates[]"]');
      if (input?.isConnected) input.value = input.dataset.value;
      showError(error, row);
      return null;
    }).finally(() => {
      pending -= 1;
      row?.removeAttribute('aria-busy');
      if (quantity === 0) removals.delete(token);
      unlockCheckout();
    });
    queue = result;
    if (quantity === 0) removals.set(token, result);
    return result;
  };

  window.SnugCartMutations = { change };
  document.addEventListener('click', (event) => {
    const button = event.target.closest?.('mini-cart-remove-button, cart-remove-button, .btn-minicart__remove-js');
    if (!button || button.classList.contains('action-add-wishlist')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const row = button.closest('.cart-item');
    change({ key: row?.dataset.snugKey || row?.querySelector('[name="updates[]"]')?.dataset.id || button.dataset.index, quantity: 0, target: button });
  }, true);
  customElements.whenDefined('cart-notification').then(() => {
    customElements.get('cart-notification').prototype.updateQuantity = function (key, quantity, previous, target) {
      return change({ key, quantity, target });
    };
  });
  customElements.whenDefined('cart-items').then(() => {
    customElements.get('cart-items').prototype.updateQuantity = function (line, key, quantity, name, target) {
      return change({ line, key: typeof key === 'string' && key.includes(':') ? key : target?.dataset.id, quantity: quantity === undefined ? 0 : quantity, target });
    };
  });
  document.addEventListener('cart:added', () => { lastState = null; });
})();
